import { useState, useRef, useCallback } from 'react';

/**
 * useDeepgram — real-time speech-to-text via Deepgram Nova-2 WebSocket.
 *
 * Browser connects directly to Deepgram's WebSocket API. The HAL Voice worker
 * provides the API key via /api/deepgram-token so it stays out of frontend code.
 *
 * Audio capture: getUserMedia → AudioContext (16kHz) → ScriptProcessor → PCM int16 → WebSocket.
 * Noise gate: RMS volume detection — only forwards audio when level exceeds threshold.
 *
 * Interface matches the previous Web Speech API version:
 *   connect(workerUrl)  — fetch key, open WebSocket, start mic capture
 *   disconnect()        — close everything
 *   resume()            — clear state, keep WebSocket open for next utterance
 *   onTranscriptRef     — callback ref for (text, isFinal) events
 */
export default function useDeepgram() {
  const [transcript, setTranscript] = useState('');
  const [partialTranscript, setPartialTranscript] = useState('');
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [micLevel, setMicLevel] = useState(0); // 0-1 normalized RMS level
  const [noiseGateOpen, setNoiseGateOpen] = useState(false);

  const onTranscriptRef = useRef(null);
  const wsRef = useRef(null);
  const audioContextRef = useRef(null);
  const processorRef = useRef(null);
  const streamRef = useRef(null);
  const sourceRef = useRef(null);
  const analyserRef = useRef(null);
  const levelIntervalRef = useRef(null);
  // Track whether we already fired a final transcript for this utterance
  const firedFinalRef = useRef(false);
  // Noise gate threshold in RMS (0-1). Default ~0.01 corresponds to roughly -40dB.
  const noiseGateThresholdRef = useRef(0.03);
  // Track gate state in ref for audio callback
  const gateOpenRef = useRef(false);
  // Mute state ref (controlled externally via mute/unmute)
  const mutedRef = useRef(false);

  /**
   * Stop mic capture and audio processing (but leave WebSocket open).
   */
  const stopAudio = useCallback(() => {
    if (levelIntervalRef.current) {
      clearInterval(levelIntervalRef.current);
      levelIntervalRef.current = null;
    }
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current.onaudioprocess = null;
      processorRef.current = null;
    }
    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }
    if (analyserRef.current) {
      analyserRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setMicLevel(0);
    setNoiseGateOpen(false);
    gateOpenRef.current = false;
  }, []);

  /**
   * Start mic capture and pipe PCM frames to the Deepgram WebSocket.
   * Includes noise gate — only sends audio when RMS exceeds threshold.
   */
  const startAudio = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        sampleRate: 16000,
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
      },
    });
    streamRef.current = stream;

    const audioContext = new AudioContext({ sampleRate: 16000 });
    audioContextRef.current = audioContext;

    const source = audioContext.createMediaStreamSource(stream);
    sourceRef.current = source;

    // Create AnalyserNode for RMS level metering
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 2048;
    analyser.smoothingTimeConstant = 0.3;
    analyserRef.current = analyser;
    source.connect(analyser);

    // Periodically update mic level for UI
    const dataArray = new Float32Array(analyser.fftSize);
    levelIntervalRef.current = setInterval(() => {
      if (!analyserRef.current) return;
      analyserRef.current.getFloatTimeDomainData(dataArray);
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        sum += dataArray[i] * dataArray[i];
      }
      const rms = Math.sqrt(sum / dataArray.length);
      setMicLevel(Math.min(1, rms * 10)); // Scale for UI (0-1)
      const isOpen = rms >= noiseGateThresholdRef.current;
      gateOpenRef.current = isOpen;
      setNoiseGateOpen(isOpen);
    }, 50);

    // ScriptProcessor: 4096 samples, 1 input channel, 1 output channel
    const processor = audioContext.createScriptProcessor(4096, 1, 1);
    processorRef.current = processor;

    analyser.connect(processor);
    processor.connect(audioContext.destination);

    // Holdover: keep sending audio for a short time after gate closes
    // to avoid cutting off speech endings
    let holdoverFrames = 0;
    const HOLDOVER_COUNT = 5; // ~5 frames * 256ms = ~1.3s holdover

    processor.onaudioprocess = (e) => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      // If muted externally, don't send
      if (mutedRef.current) return;

      const float32 = e.inputBuffer.getChannelData(0);

      // Calculate frame RMS for noise gate decision
      let sum = 0;
      for (let i = 0; i < float32.length; i++) {
        sum += float32[i] * float32[i];
      }
      const frameRms = Math.sqrt(sum / float32.length);
      const threshold = noiseGateThresholdRef.current;

      if (frameRms >= threshold) {
        holdoverFrames = HOLDOVER_COUNT;
      } else if (holdoverFrames > 0) {
        holdoverFrames--;
      }

      // Only send audio if gate is open or in holdover period
      if (frameRms >= threshold || holdoverFrames > 0) {
        const int16 = new Int16Array(float32.length);
        for (let i = 0; i < float32.length; i++) {
          const s = Math.max(-1, Math.min(1, float32[i]));
          int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }
        ws.send(int16.buffer);
      }
    };
  }, []);

  /**
   * setNoiseGateThreshold — configure noise gate sensitivity.
   * @param {number} threshold — RMS threshold 0-1 (default 0.01 = ~-40dB)
   */
  const setNoiseGateThreshold = useCallback((threshold) => {
    noiseGateThresholdRef.current = Math.max(0, Math.min(1, threshold));
  }, []);

  /**
   * connect(workerUrl) — fetch Deepgram key from worker, open WebSocket, start mic.
   */
  const connect = useCallback(
    async (workerUrl) => {
      // Derive HTTP base URL from the worker URL
      const httpBase = (workerUrl || '')
        .replace('ws://', 'http://')
        .replace('wss://', 'https://')
        .replace(/\/ws\/?$/, '');

      // Fetch Deepgram API key from worker
      let apiKey;
      try {
        const res = await fetch(`${httpBase}/api/deepgram-token`);
        if (!res.ok) throw new Error(`Token endpoint returned ${res.status}`);
        const data = await res.json();
        apiKey = data.key;
      } catch (err) {
        console.error('[Deepgram] Failed to fetch API key:', err);
        return;
      }

      // Build Deepgram WebSocket URL
      const dgParams = new URLSearchParams({
        model: 'nova-2',
        language: 'en',
        smart_format: 'true',
        interim_results: 'true',
        utterance_end_ms: '3500',
        vad_events: 'true',
        endpointing: '1500',
        encoding: 'linear16',
        sample_rate: '16000',
        channels: '1',
      });
      const dgUrl = `wss://api.deepgram.com/v1/listen?${dgParams.toString()}`;

      const ws = new WebSocket(dgUrl, ['token', apiKey]);
      wsRef.current = ws;
      firedFinalRef.current = false;

      ws.onopen = async () => {
        console.log('[Deepgram] WebSocket connected');
        setIsTranscribing(true);
        try {
          await startAudio();
        } catch (err) {
          console.error('[Deepgram] Mic access error:', err);
        }
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);

          // Deepgram speech_started event
          if (msg.type === 'SpeechStarted') {
            firedFinalRef.current = false;
            return;
          }

          // Deepgram UtteranceEnd event
          if (msg.type === 'UtteranceEnd') {
            return;
          }

          // Transcript result
          if (msg.type === 'Results' && msg.channel) {
            const alt = msg.channel.alternatives?.[0];
            if (!alt) return;
            const text = alt.transcript || '';
            if (!text) return;

            const isFinal = msg.is_final;
            const speechFinal = msg.speech_final;

            if (isFinal && speechFinal) {
              const trimmed = text.trim();
              if (trimmed && !firedFinalRef.current) {
                firedFinalRef.current = true;
                setTranscript(trimmed);
                setPartialTranscript('');
                onTranscriptRef.current?.(trimmed, true);
              }
            } else if (isFinal) {
              setPartialTranscript(text);
              onTranscriptRef.current?.(text, false);
            } else {
              setPartialTranscript(text);
              onTranscriptRef.current?.(text, false);
            }
          }
        } catch (err) {
          console.error('[Deepgram] Message parse error:', err);
        }
      };

      ws.onerror = (err) => {
        console.error('[Deepgram] WebSocket error:', err);
      };

      ws.onclose = (event) => {
        console.log('[Deepgram] WebSocket closed:', event.code, event.reason);
        setIsTranscribing(false);
        stopAudio();
        wsRef.current = null;
      };
    },
    [startAudio, stopAudio]
  );

  /**
   * mute() — disable mic track so HAL's voice doesn't echo back.
   */
  const mute = useCallback(() => {
    mutedRef.current = true;
    if (streamRef.current) {
      streamRef.current.getAudioTracks().forEach(t => { t.enabled = false; });
    }
  }, []);

  /**
   * unmute() — re-enable mic track.
   */
  const unmute = useCallback(() => {
    mutedRef.current = false;
    if (streamRef.current) {
      streamRef.current.getAudioTracks().forEach(t => { t.enabled = true; });
    }
  }, []);

  /**
   * resume() — clear state for next utterance. WebSocket stays open.
   */
  const resume = useCallback(() => {
    firedFinalRef.current = false;
    setPartialTranscript('');
    setTranscript('');
    unmute();
    setIsTranscribing(true);
  }, [unmute]);

  /**
   * disconnect() — close WebSocket + mic, full teardown.
   */
  const disconnect = useCallback(() => {
    stopAudio();
    if (wsRef.current) {
      try {
        wsRef.current.send(JSON.stringify({ type: 'CloseStream' }));
      } catch (_) {}
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsTranscribing(false);
    setPartialTranscript('');
    setTranscript('');
    setMicLevel(0);
    setNoiseGateOpen(false);
    firedFinalRef.current = false;
  }, [stopAudio]);

  const sendAudio = useCallback(() => {}, []);

  return {
    transcript,
    partialTranscript,
    isTranscribing,
    micLevel,
    noiseGateOpen,
    connect,
    disconnect,
    mute,
    unmute,
    resume,
    sendAudio,
    onTranscriptRef,
    setNoiseGateThreshold,
  };
}
